import classNames from 'classnames'
import moment from 'moment'

import {
  QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_COUNT,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_INFINITE,
  useManagementGetTaskQueue
} from '@apps-common/apis/management/unit-task/task-queue'
import { useManagementCancelUnitTaskQueue } from '@apps-common/apis/management/unit-task/task-queue/cancel-unit-task-queue'
import { useManagementDeleteUnitTaskQueue } from '@apps-common/apis/management/unit-task/task-queue/delete-unit-task-queue'
import { TaskStatusWidget } from '@apps-common/apps/portal/pages/units/pages/program-tasks/components'
import { ButtonRoundSm, INPUT_INFO_SM_NO_BORDER_NO_BG, InputInfoSM } from '@apps-common/components/elements'
import { ConfirmModal, ModalButton } from '@apps-common/components/modals'
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_TASK_QUEUE_STATUS_CANCELLABLE_STATUSES, UNIT_TASK_QUEUE_STATUS_DELETABLE_STATUSES } from '@apps-common/config/unit-task'
import { queryClient } from '@apps-common/lib'
import { IUnitTaskQueueModel } from '@apps-common/types/portal/unit-tasks'

interface IComponentProps {
  id: number
}

export const TaskQueueDetailsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IComponentProps
}) => {
  const { id } = componentProps

  const onSuccess = (data?: IUnitTaskQueueModel) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_GET_TASK_QUEUE(data?.id) })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_COUNT() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_QUEUE_INFINITE() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const {
    data: queueItem,
    isLoading: queueItemIsLoading,
    isError: queueItemIsError
  } = useManagementGetTaskQueue({ config: { enabled: !!id && !Number.isNaN(id) }, id })

  const { mutate: cancelItem, isPending: cancelIsPending } = useManagementCancelUnitTaskQueue(true, (data) => onSuccess(data))
  const { mutate: deleteItem, isPending: deleteIsPending } = useManagementDeleteUnitTaskQueue(true, () => onSuccess())

  const loading = queueItemIsLoading || queueItemIsError
  const updating = cancelIsPending || deleteIsPending

  return (
    <ModalContainer innerClasses='sm:!h-auto'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle='Task Queue' title='Details' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[7px]'>
            {UNIT_TASK_QUEUE_STATUS_DELETABLE_STATUSES.includes(queueItem?.status || '') && (
              <ModalButton
                component={ConfirmModal}
                disabled={updating}
                classes=''
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this item in the queue?',
                  action: 'error'
                }}
                confirmCallback={() => deleteItem({ model: queueItem as any })}
                tooltip={{ id: `task-queue-item-error-modal-${id}`, message: 'Error' }}
              >
                <ButtonRoundSm type='div' className='bg-error text-on-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            {UNIT_TASK_QUEUE_STATUS_CANCELLABLE_STATUSES.includes(queueItem?.status || '') && (
              <ModalButton
                component={ConfirmModal}
                disabled={updating}
                classes=''
                componentProps={{
                  text: 'Cancel',
                  description: 'Confirm you would like to cancel this item in the queue?',
                  action: 'warning'
                }}
                confirmCallback={() => cancelItem({ model: queueItem as any })}
                tooltip={{ id: `task-queue-item-cancel-modal-${id}`, message: 'Cancel' }}
              >
                <ButtonRoundSm type='div' className='bg-warning text-on-warning'>
                  <commonIcons.cancel />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[90px] !pb-[40px] sm:!pb-[20px]'>
        <div className='grid sm:grid-cols-2 gap-[10px]'>
          <InputInfoSM
            id='product'
            label='Product'
            showLabel
            labelClassName='ml-0 mb-1 rounded-sm jost-medium'
            className={classNames(INPUT_INFO_SM_NO_BORDER_NO_BG, '!px-0')}
            loading={loading}
            hideNoEditIcon
            value={
              <div className='flex'>
                <div className='flex items-center justify-start gap-2 p-1 pr-4 bg-surface border border-border rounded-full'>
                  <div className='rounded-full bg-surface-2 border border-border w-[45px] h-[45px] p-1 flex items-center justify-center'>
                    <img src={queueItem?.productImageUrl} alt={queueItem?.productName} className='max-w-[30px] max-h-[30px]' />
                  </div>
                  <div>{queueItem?.productName}</div>
                </div>
              </div>
            }
          />
          <InputInfoSM
            id='status'
            label='Status'
            showLabel
            className={classNames(INPUT_INFO_SM_NO_BORDER_NO_BG, 'capitalize')}
            loading={loading}
            hideNoEditIcon
            value={<TaskStatusWidget status={queueItem?.status || ''} />}
          />
          <InputInfoSM
            id='message'
            label='Message'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.message || <commonIcons.dash />}
          />
          <InputInfoSM
            id='siteName'
            label='Site'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.siteName || <commonIcons.dash />}
          />
          <InputInfoSM
            id='accountNo'
            label='Account'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.accountNo || <commonIcons.dash />}
          />
          <InputInfoSM
            id='serialNo'
            label='Serial'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.serialNo || <commonIcons.dash />}
          />
          <InputInfoSM
            id='imei'
            label='IMEI'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.imei || <commonIcons.dash />}
          />
          <InputInfoSM
            id='simNo'
            label='SIM'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.simNo || <commonIcons.dash />}
          />
          <InputInfoSM
            id='mobileNo'
            label='Mobile'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.mobileNo || <commonIcons.dash />}
          />
          <InputInfoSM
            id='ipAddress'
            label='IP'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.ipAddress || <commonIcons.dash />}
          />
          <InputInfoSM
            id='updatedBy'
            label='Updated by'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={queueItem?.updatedBy || <commonIcons.dash />}
          />
          <InputInfoSM
            id='updatedAt'
            label='Updated at'
            showLabel
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={moment(queueItem?.updatedAt).format('DD/MM/YY HH:mm') || <commonIcons.dash />}
          />
        </div>
        <div className='w-full rounded-xl border border-border text-text overflow-hidden'>
          <div className='text-sm px-4 py-2 bg-surface-2'>Commands</div>
          {queueItem?.commands &&
            queueItem?.commands?.length > 0 &&
            queueItem?.commands?.map((c, i) => (
              <div
                key={`task-queue-details-command-${c.id}-${i}`}
                className={classNames(
                  'flex items-center justify-between text-[0.85rem] px-4 py-2 gap-2',
                  i % 2 === 0 ? 'bg-surface-2' : 'bg-surface'
                )}
              >
                <div>{c.command}</div> <div>{c.value}</div>
              </div>
            ))}
          {(!queueItem?.commands || queueItem?.commands?.length === 0) && (
            <div className={classNames('flex items-center justify-between text-[0.85rem] px-4 py-2 gap-2 bg-surface')}>
              <div>No commands associated</div>
            </div>
          )}
        </div>
      </ModalBody>
    </ModalContainer>
  )
}
